import { computed, defineComponent, h } from 'vue';
import { useColors, useStringFunctions } from '@apparatix/composables';
import TextTruncate from '@apparatix/library/TextTruncate.vue';

export type TypographyVariant =
  | 'activity-heading'
  | 'address-form-label'
  | 'body'
  | 'bold-label'
  | 'breadcrumb'
  | 'dialog-heading'
  | 'drawer-subtitle'
  | 'drawer-title'
  | 'form-error'
  | 'form-heading'
  | 'form-label'
  | 'heading-subtitle'
  | 'heading'
  | 'jumbo'
  | 'link'
  | 'list-item'
  | 'page-title'
  | 'section-footer'
  | 'section-heading'
  | 'section-subheading'
  | 'small-subtitle'
  | 'small-body'
  | 'table-cell-primary'
  | 'table-cell'
  | 'stepper-title';

type Props = {
  variant: TypographyVariant;
  muted?: boolean;
  indent?: boolean;
  for?: string;
  truncate?: boolean;
}

type Emits = {
  truncated: (didTruncate: boolean) => void,
};

export default defineComponent<Props, Emits>((props, { slots, emit }) => {
  const colors = useColors();
  const { isEmptyString } = useStringFunctions();

  const isMuted = computed(() => {
    if (props.muted === undefined) return false;

    if (typeof props.muted === 'string' && isEmptyString(props.muted)) {
      return true;
    }

    return typeof props.muted === 'boolean' ? props.muted : false;
  });

  if (isMuted.value && props.variant === 'table-cell-primary' && import.meta.env.DEV) {
    console.warn('The `muted` prop is ignored for Typography variant `table-cell-colored`');
  }

  const isIndented = computed(() => {
    if (props.indent === undefined) return false;

    if (typeof props.indent === 'string' && isEmptyString(props.indent)) {
      return true;
    }

    return typeof props.indent === 'boolean' ? props.indent : false;
  });

  const shouldTruncate = computed(() => {
    if (props.truncate === undefined) return false;

    if (typeof props.truncate === 'string' && isEmptyString(props.truncate)) {
      return true;
    }

    return typeof props.truncate === 'boolean' ? props.truncate : false;
  });

  type ElementName = keyof HTMLElementTagNameMap;

  type ElementProps = {
    class: string | string[];
    for?: string;
    style: {
      color: string;
    }
  }

  type ElementOptions = {
    element: ElementName;
    props: ElementProps;
  }

  const buildOptions = (element: ElementName, classList: string, color?: string): ElementOptions => ({
    element,
    props: {
      class: [classList, isIndented.value ? 'ml-4' : ''],
      for: element === 'label' ? props.for : undefined,
      style: {
        color: color ? color :
          isMuted.value ? colors.text.muted : colors.text.primary,
      },
    },
  });

  const options = computed<ElementOptions>(() => {
    switch (props.variant) {
      case 'activity-heading':
        return { ...buildOptions('h2', 'text-lg lg:text-xl font-normal') };
      case 'body':
        return { ...buildOptions('p', 'text-sm lg:text-base font-normal') };
      case 'bold-label':
        return { ...buildOptions('p', 'text-sm font-semibold') };
      case 'breadcrumb':
        return { ...buildOptions('span', 'text-xs font-medium leading-4') };
      case 'drawer-subtitle':
        return { ...buildOptions('p', 'text-sm font-medium leading-5') };
      case 'drawer-title':
        return { ...buildOptions('h1', 'text-xl font-bold leading-7') };
      case 'form-error':
        return { ...buildOptions('small', 'text-sm font-normal', colors.text.error) };
      case 'form-heading':
      case 'stepper-title':
        return { ...buildOptions('h3', 'text-base font-medium', colors.text.primaryHover) };
      case 'form-label':
        return { ...buildOptions('label', 'text-sm font-normal') };
      case 'address-form-label':
        return { ...buildOptions('label', 'text-sm font-normal whitespace-pre-line') };
      case 'heading-subtitle':
        return { ...buildOptions('p', 'text-sm font-medium') };
      case 'heading':
        return { ...buildOptions('h1', 'text-base md:text-lg lg:text-xl font-bold') };
      case 'jumbo':
        return { ...buildOptions('h1', 'text-2xl lg:text-3xl font-bold leading-10') };
      case 'link':
        return { ...buildOptions('a', 'text-sm lg:text-base font-normal text-[var(--p-primary-color)] no-underline hover:underline') };
      case 'page-title':
        return { ...buildOptions('h1', 'text-lg font-semibold leading-7 ') };
      case 'dialog-heading':
      case 'section-heading':
        return { ...buildOptions('h2', 'text-base lg:text-lg font-semibold') };
      case 'section-subheading':
        return { ...buildOptions('p', 'text-base font-semibold') };
      case 'section-footer':
        return { ...buildOptions('p', 'text-xs lg:text-sm font-normal') };
      case 'small-subtitle':
        return { ...buildOptions('p', 'text-xs font-normal') };
      case 'small-body':
        return { ...buildOptions('p', 'text-sm font-normal') };
      case 'table-cell':
        return { ...buildOptions('span', 'text-sm font-normal') };
      case 'table-cell-primary':
        return { ...buildOptions('span', 'text-sm font-normal', colors.primary.color) };
      case 'list-item':
        return { ...buildOptions('li', 'text-sm lg:text-base font-normal') };
      default:
        return { ...buildOptions('p', '') };
    }
  });

  if (shouldTruncate.value) {
    const [child] = slots.default();
    const text = typeof child.children === 'string' ? child.children : undefined;

    return () => h(TextTruncate, { text, ...options.value.props, onTruncated: didTruncate => emit('truncated', didTruncate) });
  }

  return () => h(options.value.element, { ...options.value.props }, slots.default());
}, {
  // eslint-disable-next-line vue/require-prop-types
  props: ['variant', 'muted', 'indent', 'truncate', 'for'],
  emits: ['truncated'],
});
